import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  DfSelectSingleFilter,
  DfSelectSingleOption,
} from '../../../shared/_directives/df-select-single/df-select-single-filter.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import {
  generalConfigFromGlobal,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { Submission } from '../../models/submission.model';
import { SubmissionService } from '../../submission-data/submission.service';
import { EfileGridConfig, EfileGridDirective } from '../../shared/efile-grid.directive';
import { EfileXmlCompareService } from './efile-xml-compare.service';
import {
  buildXmlCompareGridPayload,
  buildXmlCompareExportHeaders,
  IrsXmlCompareRow,
  irsIdFromFilterList,
  selectedIrsId,
  stretchGridHeadersToHostWidth,
} from './xml-compare.util';

export type XmlCompareViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

@Component({
  selector: 'gtw-efile-xml-compare',
  templateUrl: './efile-xml-compare.component.html',
  styleUrls: ['./efile-xml-compare.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EfileXmlCompareComponent implements OnInit, OnChanges, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly appService = inject(AppService);
  private readonly gtwGlobal = inject(GlobalService);
  private readonly gtwWorkspace = inject(WorkspaceService);
  private readonly submissionService = inject(SubmissionService);
  private readonly snackbar = inject(GtwSnackbarService);
  private readonly xmlCompareService = inject(EfileXmlCompareService);

  submission = input<Submission | null>(null);

  /** When true, dropdowns live in the parent Return Review filter bar. */
  resultsOnly = input(false);

  @ViewChild('xmlCompareGrid') gridRef?: EfileGridDirective;

  readonly gridConfig: EfileGridConfig = {
    gridId: 'efile-xml-compare',
    autoLoad: false,
    delegateExcelExport: true,
  };

  private lastComparedRows: IrsXmlCompareRow[] = [];

  private readonly subs = new Subscription();

  leftFilter: DfSelectSingleFilter | null = null;
  rightFilter: DfSelectSingleFilter | null = null;
  optionsLoading = false;
  comparing = false;
  validationMessage: string | null = null;
  compareState: XmlCompareViewState = { kind: 'idle' };

  private efileBaseUrl = '';
  private gtwBaseUrl = '/gtw';
  private globalService: Record<string, unknown> = {};

  ngOnInit(): void {
    if (!this.resultsOnly()) {
      this.applyContext();
    } else {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      this.efileBaseUrl = gc.efile || '/gtw-efile-api';
      this.gtwBaseUrl = gc.base_url || '/gtw';
    }
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      if (!this.resultsOnly()) {
        this.applyContext();
      }
    }));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      if (!this.resultsOnly()) {
        this.applyContext();
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['submission'] && !this.resultsOnly()) {
      this.applyContext();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get compareErrorMessage(): string {
    return this.compareState.kind === 'error' ? this.compareState.message : '';
  }

  get showGrid(): boolean {
    return this.compareState.kind === 'success';
  }

  private columnStretchDone = false;

  onGridDataLoaded(data: any): void {
    if (!this.columnStretchDone && data?.header && this.gridRef) {
      const fixedColumns = Number(data.settings?.fixed_columns ?? 1);
      const stretched = stretchGridHeadersToHostWidth(
        data.header,
        fixedColumns,
        this.gridRef.getHostClientWidth(),
      );
      if (stretched) {
        this.columnStretchDone = true;
        this.gridRef.loadPayload({
          ...data,
          header: stretched,
          firstLoad: 0,
        });
        return;
      }
    }
    this.columnStretchDone = false;
    this.scheduleGridRelayout();
  }

  onLeftFilterChange(filter: DfSelectSingleFilter): void {
    this.leftFilter = filter;
    this.clearResults();
    this.cdr.markForCheck();
  }

  onRightFilterChange(filter: DfSelectSingleFilter): void {
    this.rightFilter = filter;
    this.clearResults();
    this.cdr.markForCheck();
  }

  reloadOptions(): void {
    this.loadIrsOptions(true);
  }

  compareFromParentFilters(parentFilters: DfSelectSingleFilter[]): string | null {
    this.validationMessage = null;
    this.compareState = { kind: 'idle' };

    const leftId = irsIdFromFilterList(parentFilters, 'irs_id');
    const rightId = irsIdFromFilterList(parentFilters, 'irs_id_2');

    const validationError = this.validateCompareIds(leftId, rightId);
    if (validationError) {
      this.validationMessage = validationError;
      this.cdr.markForCheck();
      return validationError;
    }

    this.runCompare(leftId, rightId);
    return null;
  }

  compare(): void {
    this.validationMessage = null;
    this.compareState = { kind: 'idle' };

    const leftId = selectedIrsId(this.leftFilter);
    const rightId = selectedIrsId(this.rightFilter);
    const validationError = this.validateCompareIds(leftId, rightId);
    if (validationError) {
      this.validationMessage = validationError;
      this.cdr.markForCheck();
      return;
    }

    this.runCompare(leftId, rightId);
  }

  onExcelExport(mode: 'all' | 'filtered'): void {
    if (!this.lastComparedRows.length) {
      this.snackbar.openSnackBar('Run Compare before exporting to Excel.', 'gtw-snackbar--yellow');
      this.gridRef?.finishExcelExport();
      return;
    }

    const rowsToExport = mode === 'filtered'
      ? this.getFilteredGridRows()
      : this.lastComparedRows;

    if (!rowsToExport.length) {
      this.snackbar.openSnackBar('No filtered rows to export.', 'gtw-snackbar--yellow');
      this.gridRef?.finishExcelExport();
      return;
    }

    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
    const screenKey = String(activeScreen?.screen_key || '');
    if (!screenKey) {
      this.snackbar.openSnackBar('Export is unavailable: missing screen_key.', 'gtw-snackbar--red');
      this.gridRef?.finishExcelExport();
      return;
    }

    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const headers = buildXmlCompareExportHeaders();
    this.subs.add(
      this.xmlCompareService
        .exportCompareRowsToExcel(
          this.gtwBaseUrl,
          screenKey,
          headers,
          rowsToExport,
          ssoId,
        )
        .subscribe({
          next: () => this.gridRef?.finishExcelExport(),
          error: (err: Error) => {
            this.snackbar.openSnackBar(
              err?.message || 'Error occurred while exporting. Please try again.',
              'gtw-snackbar--red',
            );
            this.gridRef?.finishExcelExport();
          },
        }),
    );
  }

  /** Filtered rows currently shown in gizmoGrid, same source as EfileGridDirective.exportToExcelFiltered. */
  private getFilteredGridRows(): IrsXmlCompareRow[] {
    const dataArr: unknown = this.gridRef?.api?.getExportData?.()?.dataArr;
    return Array.isArray(dataArr) ? dataArr as IrsXmlCompareRow[] : [];
  }

  private validateCompareIds(leftId: string, rightId: string): string | null {
    if (!leftId && !rightId) {
      return 'Select an IRS submission in both dropdowns before comparing.';
    }
    if (!leftId) {
      return 'Select the IRS Submissions dropdown before comparing.';
    }
    if (!rightId) {
      return 'Select the Compare with XML dropdown before comparing.';
    }
    return null;
  }

  private runCompare(leftId: string, rightId: string): void {
    if (leftId === rightId) {
      this.comparing = false;
      this.compareState = { kind: 'empty' };
      this.cdr.markForCheck();
      return;
    }

    const submissionId = this.resolveSubmissionId();
    if (!submissionId) {
      this.validationMessage = 'Select a submission before comparing IRS XML files.';
      this.cdr.markForCheck();
      return;
    }

    this.comparing = true;
    this.compareState = { kind: 'loading' };
    this.lastComparedRows = [];
    this.cdr.markForCheck();

    this.subs.add(
      this.xmlCompareService
        .compareIrsXml(this.efileBaseUrl, submissionId, leftId, rightId)
        .subscribe({
          next: (res) => {
            this.comparing = false;
            const rows = Array.isArray(res?.data) ? res.data : [];
            if (rows.length === 0) {
              this.lastComparedRows = [];
              this.compareState = { kind: 'empty' };
              this.cdr.markForCheck();
              return;
            }

            this.lastComparedRows = rows;
            this.compareState = { kind: 'success' };
            this.cdr.detectChanges();
            // Mount the grid host first, then load after layout so gizmoGrid
            // measures a real width/height (zero-size hosts break header/body paint).
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.loadGridRows(rows);
                this.scheduleGridRelayout();
                this.cdr.markForCheck();
              });
            });
          },
          error: (err: Error) => {
            this.comparing = false;
            const message =
              err?.message ||
              'Comparison failed. One of the selected files may be unavailable or invalid.';
            this.compareState = { kind: 'error', message };
            this.snackbar.openSnackBar(message, 'gtw-snackbar--red');
            this.cdr.markForCheck();
          },
        }),
    );
  }

  private loadGridRows(rows: unknown[], attempt = 0): void {
    if (!this.gridRef) {
      if (attempt < 12) {
        setTimeout(() => this.loadGridRows(rows, attempt + 1), 0);
      }
      return;
    }
    this.columnStretchDone = false;
    const payload = buildXmlCompareGridPayload(rows as IrsXmlCompareRow[]);
    this.gridRef.loadPayload(payload);
    this.scheduleGridRelayout();
  }

  private scheduleGridRelayout(): void {
    const relayout = () => this.gridRef?.relayout();
    setTimeout(relayout, 0);
    setTimeout(relayout, 80);
    setTimeout(relayout, 250);
  }

  private resolveSubmissionId(): string | number | null {
    const sub =
      this.submission()?.SUBMISSION_ID != null
        ? this.submission()
        : this.submissionService.activeSubmission;
    return sub?.SUBMISSION_ID ?? null;
  }

  private clearResults(): void {
    this.validationMessage = null;
    this.lastComparedRows = [];
    if (this.compareState.kind !== 'loading') {
      this.compareState = { kind: 'idle' };
    }
  }

  private applyContext(): void {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.efileBaseUrl = gc.efile || '/gtw-efile-api';
    this.gtwBaseUrl = gc.base_url || '/gtw';
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    normalizeFilterParams(activeScreen);
    this.globalService = this.gtwGlobal.global || {};

    const sub =
      this.submission()?.SUBMISSION_ID != null
        ? this.submission()
        : this.submissionService.activeSubmission;

    if (!sub?.SUBMISSION_ID) {
      this.leftFilter = this.makeEmptyFilter('irs_id', 'IRS Submissions');
      this.rightFilter = this.makeEmptyFilter('irs_id_2', 'Compare with XML');
      this.cdr.markForCheck();
      return;
    }

    if (!this.leftFilter) {
      this.leftFilter = this.makeEmptyFilter('irs_id', 'IRS Submissions');
    }
    if (!this.rightFilter) {
      this.rightFilter = this.makeEmptyFilter('irs_id_2', 'Compare with XML');
    }

    this.loadIrsOptions();
  }

  private loadIrsOptions(force = false): void {
    const sub = this.submission()?.SUBMISSION_ID
      ? this.submission()
      : this.submissionService.activeSubmission;
    if (!sub?.SUBMISSION_ID) {
      return;
    }
    if (this.optionsLoading && !force) {
      return;
    }

    const baseUrl = generalConfigFromGlobal(this.gtwGlobal).base_url || '/gtw';
    this.optionsLoading = true;
    if (this.leftFilter) {
      this.leftFilter = { ...this.leftFilter, loading: true };
    }
    if (this.rightFilter) {
      this.rightFilter = { ...this.rightFilter, loading: true };
    }
    this.cdr.markForCheck();

    this.subs.add(
      this.xmlCompareService
        .loadIrsSubmissionOptions(baseUrl, sub.SUBMISSION_ID, this.globalService)
        .subscribe({
          next: (rows) => {
            this.leftFilter = this.buildFilter(rows, this.leftFilter, 'irs_id', 'IRS Submissions');
            this.rightFilter = this.buildFilter(rows, this.rightFilter, 'irs_id_2', 'Compare with XML');
            this.optionsLoading = false;
            this.cdr.markForCheck();
          },
          error: () => {
            this.leftFilter = this.buildFilter([], this.leftFilter, 'irs_id', 'IRS Submissions');
            this.rightFilter = this.buildFilter([], this.rightFilter, 'irs_id_2', 'Compare with XML');
            this.optionsLoading = false;
            this.snackbar.openSnackBar('Unable to load IRS submission list.', 'gtw-snackbar--red');
            this.cdr.markForCheck();
          },
        }),
    );
  }

  private makeEmptyFilter(paramName: string, label: string): DfSelectSingleFilter {
    return {
      label,
      loading: true,
      values: [],
      selected: [],
      param_name: paramName,
      label_name: 'LABEL',
      value_name: 'VALUE',
    };
  }

  private buildFilter(
    rows: DfSelectSingleOption[],
    previous: DfSelectSingleFilter | null,
    paramName: string,
    label: string,
  ): DfSelectSingleFilter {
    const preserveId = selectedIrsId(previous);
    let selected: DfSelectSingleOption[] = [];
    if (preserveId) {
      const match = rows.find((r) => String(r.VALUE) === preserveId);
      if (match) {
        selected = [match];
      }
    }
    return {
      ...this.makeEmptyFilter(paramName, label),
      values: rows,
      selected,
      loading: false,
    };
  }
}
